/**
 * For this area (slice 6d spec §9; Paper A1): each app with an account the
 * person can use, and the one the crew uses first in a chat in the Area.
 * Use for this area picks it; "Usual order" leaves the crew's own choice
 * (the asker's primary, then a shared one).
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@crew/ui/components/select";
import { TextLink } from "@crew/ui/components/typography";
import { errorHint } from "../../app/api";
import { usePreferAccount, type AreaApp } from "./api";

const USUAL = "";

/** An account's line in the picker: its label, and whose when it's shared. */
const accountLine = (a: AreaApp["accounts"][number]) =>
  a.shared_by ? `${a.label} · ${a.shared_by}'s` : a.label;

function AppRow({ app, areaId, archived }: { app: AreaApp; areaId: string; archived: boolean }) {
  const prefer = usePreferAccount(areaId);
  const items = [
    { value: USUAL, label: "Usual order" },
    ...app.accounts.map((a) => ({ value: a.id, label: accountLine(a) })),
  ];
  return (
    <li className="flex flex-col gap-1.5" data-area-app={app.app}>
      <span className="text-body font-medium text-foreground">{app.name}</span>
      <Select
        items={items}
        value={app.preferred ?? USUAL}
        disabled={archived || prefer.isPending}
        onValueChange={(v) =>
          typeof v === "string" && prefer.mutate({ app: app.app, accountId: v || null })
        }
      >
        <SelectTrigger
          aria-label={`${app.name}: used first here`}
          className="h-8.5 w-full text-meta"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent alignItemWithTrigger={false} align="start">
          {items.map((item) => (
            <SelectItem key={item.value} value={item.value} className="text-body">
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {prefer.error ? (
        <span className="text-meta text-brick">{errorHint(prefer.error)}</span>
      ) : null}
    </li>
  );
}

export function AreaApps({
  apps,
  areaId,
  archived,
}: {
  apps: readonly AreaApp[];
  areaId: string;
  /** An archived Area's choices show, but change only once it's restored. */
  archived: boolean;
}) {
  if (apps.length === 0) {
    return (
      <p className="text-meta text-muted-foreground">
        No apps connected yet. <TextLink href="/settings/integrations">Connect one</TextLink>
      </p>
    );
  }
  return (
    <>
      <ul className="flex flex-col gap-3">
        {apps.map((app) => (
          <AppRow key={app.app} app={app} areaId={areaId} archived={archived} />
        ))}
      </ul>
      <p className="text-meta text-muted-foreground">
        {archived
          ? "Restore the area to change these."
          : "The account the crew uses first in this area's chats."}
      </p>
    </>
  );
}
